import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { stockTrucksApi, Truck, TruckDay, TruckRoute, TrucksSummary } from '../services/stockTrucksApi';
import { fmtDay, fmtInt, fmtLocalDateTime } from '../utils/replenishment';

// Presets de período (dias do camião)
const PRESETS: Array<[number, string]> = [[28, '4 semanas'], [91, '13 semanas'], [182, '6 meses'], [365, '12 meses']];
const DEFAULT_PRESET = 28;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const WEEKDAYS = ['', 'Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
// Rotas com capacidade (camião); Leiria vai na carrinha interna
const TRUCK_ROUTES: TruckRoute[] = ['Norte', 'Sul'];
const ROUTE_COLORS: Record<string, string> = { Norte: '#2a78d6', Sul: '#eb6834' };
const ROUTE_STORES: Record<TruckRoute, string> = {
  Norte: 'Gaia, Porto, Braga', Sul: 'Expo, Benfica', Leiria: 'carrinha interna',
};
const HIGH_PCT = 70;
// Nomes das lojas (email 6263, T1437)
const STORE_SHORT: Record<string, string> = {
  A000: 'Leiria', B000: 'Expo', C000: 'Porto', D000: 'Benfica', E000: 'Braga', F000: 'Gaia',
};
const storeName = (code: string): string => STORE_SHORT[code] || code;

const lisbonToday = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Lisbon' });

const shiftDate = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// Segunda-feira da semana do dia (ISO)
const weekStart = (isoDate: string): string => {
  const d = new Date(`${isoDate}T12:00:00Z`);
  return shiftDate(isoDate, -((d.getUTCDay() + 6) % 7));
};

const fmtM3 = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : `${v.toLocaleString('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m³`;
const fmtOcc = (v: number | null | undefined): string => (v === null || v === undefined ? '-' : `${Math.round(v)}%`);
const fmtDate = (iso: string): string => iso.split('-').reverse().join('/');

const occTone = (pct: number | null | undefined): string =>
  pct === null || pct === undefined ? 'secondary' : pct >= 90 ? 'danger' : pct >= HIGH_PCT ? 'warning' : 'success';

const OccBar: React.FC<{ pct: number | null }> = ({ pct }) => (
  pct === null ? <span className="text-muted">-</span> : (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 140 }}>
      <div className="progress flex-grow-1" style={{ height: 8 }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`progress-bar bg-${occTone(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="small fw-semibold" style={{ width: 40 }}>{fmtOcc(pct)}</span>
    </div>
  )
);

interface RouteStats {
  rota: TruckRoute;
  n: number;
  last?: Truck;
  max?: Truck;
  avgM3: number;
  avgPct: number | null;
  high: number;
}

const routeStats = (trucks: Truck[], rota: TruckRoute): RouteStats => {
  const rows = trucks.filter((t) => t.rota === rota);
  const withPct = rows.filter((t) => t.ocupacao_pct !== null);
  return {
    rota,
    n: rows.length,
    last: rows[rows.length - 1],
    max: rows.reduce<Truck | undefined>((best, t) => (!best || t.m3 > best.m3 ? t : best), undefined),
    avgM3: rows.length ? rows.reduce((s, t) => s + t.m3, 0) / rows.length : 0,
    avgPct: withPct.length ? withPct.reduce((s, t) => s + (t.ocupacao_pct || 0), 0) / withPct.length : null,
    high: withPct.filter((t) => (t.ocupacao_pct || 0) >= HIGH_PCT).length,
  };
};

const RouteCard: React.FC<{ s: RouteStats }> = ({ s }) => (
  <div className="col-12 col-md-4">
    <div className={`card h-100 border-${occTone(s.last?.ocupacao_pct)}`}>
      <div className="card-body">
        <div className="d-flex justify-content-between">
          <div className="fw-semibold">
            {ROUTE_COLORS[s.rota] && <span className="d-inline-block rounded me-2" style={{ width: 10, height: 10, background: ROUTE_COLORS[s.rota] }} />}
            {s.rota} <span className="text-muted small fw-normal">· {ROUTE_STORES[s.rota]}</span>
          </div>
          <div className="text-muted small">{s.n} camiões</div>
        </div>
        {s.last ? (
          <>
            <div className="text-muted small mt-2">Último camião ({WEEKDAYS[s.last.dia_semana]} {fmtDate(s.last.dia)})</div>
            <div className={`fs-3 fw-semibold text-${occTone(s.last.ocupacao_pct)}`}>
              {s.last.ocupacao_pct !== null ? fmtOcc(s.last.ocupacao_pct) : fmtM3(s.last.m3)}
            </div>
            <div className="text-muted small">
              {fmtM3(s.last.m3)} · {s.last.paletes_equiv.toLocaleString('pt-PT')} paletes · {fmtInt(s.last.unidades)} un.
            </div>
            <hr className="my-2" />
            <div className="small">
              Média: <strong>{s.avgPct !== null ? fmtOcc(s.avgPct) : fmtM3(s.avgM3)}</strong>
              {s.avgPct !== null && <> ({fmtM3(s.avgM3)})</>}
              {s.max && (
                <> · Máximo: <strong>{s.max.ocupacao_pct !== null ? fmtOcc(s.max.ocupacao_pct) : fmtM3(s.max.m3)}</strong>
                  {' '}({fmtDate(s.max.dia)})</>
              )}
              {s.avgPct !== null && <> · ≥ {HIGH_PCT}%: <strong>{s.high}</strong></>}
            </div>
          </>
        ) : <div className="text-muted mt-2">Sem camiões no período</div>}
      </div>
    </div>
  </div>
);

const DayModal: React.FC<{ dia: string; rota?: TruckRoute; onClose: () => void }> = ({ dia, rota, onClose }) => {
  const [data, setData] = useState<TruckDay | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    stockTrucksApi.getDay(dia, rota)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar o camião'));
    return () => { alive = false; };
  }, [dia, rota]);

  const total = data ? data.by_cat1.reduce((s, c) => s + c.m3, 0) : 0;

  return (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,.4)' }} onClick={onClose}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Camião {rota || '(todas as rotas)'} — {fmtDate(dia)}</h5>
            <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-warning">{error}</div>}
            {!data && !error && <div className="text-muted">A carregar…</div>}
            {data && (
              <>
                <h6>Por categoria</h6>
                <table className="table table-sm w-auto mb-3">
                  <thead className="table-light">
                    <tr><th>Categoria</th><th className="text-end">Unidades</th><th className="text-end">Volume</th><th className="text-end">% do volume</th></tr>
                  </thead>
                  <tbody>
                    {data.by_cat1.map((c) => (
                      <tr key={c.cat1}>
                        <td>{c.cat1}</td><td className="text-end">{fmtInt(c.unidades)}</td>
                        <td className="text-end">{fmtM3(c.m3)}</td>
                        <td className="text-end">{total ? `${Math.round((c.m3 / total) * 100)}%` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h6>Produtos por volume</h6>
                {data.truncated && <div className="alert alert-info py-1 small">Mostrados só os 300 produtos com mais volume.</div>}
                <div className="table-responsive">
                  <table className="table table-sm table-hover small">
                    <thead className="table-light">
                      <tr>
                        <th>SKU</th><th>Produto</th><th>Categoria</th><th>Lojas</th><th className="text-end">Un.</th>
                        <th className="text-end">dm³/un.</th><th className="text-end">Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.products.map((p) => (
                        <tr key={p.sku}>
                          <td className="text-nowrap">{p.sku}</td>
                          <td>{p.nome || '-'}</td>
                          <td>{p.cat2 || p.cat1 || '-'}</td>
                          <td className="text-nowrap">{p.lojas.map(storeName).join(', ')}</td>
                          <td className="text-end">{p.unidades}</td>
                          <td className="text-end">
                            {p.vol_dm3.toLocaleString('pt-PT', { maximumFractionDigits: 1 })}
                            {p.vol_fonte !== 'sap' && (
                              <span className="badge bg-warning text-dark ms-1"
                                title={p.vol_fonte === 'anomalo'
                                  ? `Volume SAP anómalo (${p.vol_sap?.toLocaleString('pt-PT')} dm³): usada a mediana da categoria`
                                  : 'Sem volume no SAP: usada a mediana da categoria'}>
                                {p.vol_fonte === 'anomalo' ? 'anómalo' : 'estimado'}
                              </span>
                            )}
                          </td>
                          <td className="text-end fw-semibold">{fmtM3(p.m3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StockTrucksPage: React.FC = () => {
  const today = useMemo(lisbonToday, []);
  const [params, setParams] = useSearchParams();

  // Estado partilhável no URL
  const preset = PRESETS.some(([d]) => d === Number(params.get('preset'))) ? Number(params.get('preset')) : DEFAULT_PRESET;
  // O camião de amanhã já pode estar carregado (641 lançadas hoje)
  const end = ISO_DATE.test(params.get('end') || '') ? params.get('end')! : shiftDate(today, 1);
  const start = ISO_DATE.test(params.get('start') || '') ? params.get('start')! : shiftDate(end, -(preset - 1));
  const dayParam = params.get('dia');
  const routeParam = (['Norte', 'Sul', 'Leiria'].includes(params.get('rota') || '') ? params.get('rota') : undefined) as TruckRoute | undefined;

  const patchParams = useCallback((changes: Record<string, string>, push = false) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: !push });
  }, [setParams]);

  const [data, setData] = useState<TrucksSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    stockTrucksApi.getSummary(start, end)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar os camiões'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [start, end]);

  const trucks = useMemo(() => data?.trucks || [], [data]);
  const capacity = trucks.find((t) => t.m3_capacidade)?.m3_capacidade ?? null;
  const stats = useMemo(() => (['Norte', 'Sul', 'Leiria'] as TruckRoute[]).map((r) => routeStats(trucks, r)), [trucks]);

  const chartData = useMemo(() => {
    const byDay = new Map<string, Record<string, number | string>>();
    trucks.filter((t) => TRUCK_ROUTES.includes(t.rota)).forEach((t) => {
      const row = byDay.get(t.dia) || { dia: t.dia, label: `${WEEKDAYS[t.dia_semana]} ${fmtDay(t.dia)}` };
      row[t.rota] = t.m3;
      byDay.set(t.dia, row);
    });
    return Array.from(byDay.values());
  }, [trucks]);

  const weeks = useMemo(() => {
    const map = new Map<string, { semana: string; rota: TruckRoute; n: number; m3: number; max: number; un: number; pct: number[] }>();
    trucks.forEach((t) => {
      const key = `${weekStart(t.dia)}|${t.rota}`;
      const w = map.get(key) || { semana: weekStart(t.dia), rota: t.rota, n: 0, m3: 0, max: 0, un: 0, pct: [] };
      w.n += 1; w.m3 += t.m3; w.un += t.unidades; w.max = Math.max(w.max, t.m3);
      if (t.ocupacao_pct !== null) w.pct.push(t.ocupacao_pct);
      map.set(key, w);
    });
    return Array.from(map.values()).sort((a, b) => (b.semana.localeCompare(a.semana) || a.rota.localeCompare(b.rota)));
  }, [trucks]);

  const recent = useMemo(() => [...trucks].reverse(), [trucks]);

  return (
    <div className="pb-5">
      <div className="d-flex flex-wrap justify-content-between align-items-end mb-2 gap-2">
        <div>
          <h2 className="mb-0">🚚 Volumetria por camião</h2>
          <div className="text-muted small">
            Volume das transferências T000 → loja por camião: <strong>Norte</strong> (Gaia, Porto, Braga) e <strong>Sul</strong> (Expo,
            Benfica), com capacidade de {capacity ? `${fmtM3(capacity)} (12 paletes × ${data?.pallet_m3?.toLocaleString('pt-PT')} m³)` : '12 paletes'};
            Leiria vai na carrinha interna. Dia do camião = dia útil seguinte ao lançamento da transferência.
            Volume = soma das caixas (sem o espaço perdido a montar as paletes).
          </div>
        </div>
        <div className="text-muted small text-end">
          {data?.min_date && <>Dados de {fmtDate(data.min_date)} a {fmtDate(data.max_date || '')}<br /></>}
          {data?.calculated_at && <>Actualizado: {fmtLocalDateTime(data.calculated_at)}</>}
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div>
            <div className="small text-muted">Período</div>
            <div className="btn-group btn-group-sm">
              {PRESETS.map(([days, label]) => (
                <button key={days} className={`btn ${!params.get('start') && preset === days ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => patchParams({ preset: days === DEFAULT_PRESET ? '' : String(days), start: '', end: '' })}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="tr-start">De</label>
            <input id="tr-start" type="date" className="form-control form-control-sm" value={start}
              onChange={(e) => patchParams({ start: e.target.value, end })} />
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="tr-end">Até</label>
            <input id="tr-end" type="date" className="form-control form-control-sm" value={end}
              onChange={(e) => patchParams({ end: e.target.value, start })} />
          </div>
          {loading && <div className="spinner-border spinner-border-sm text-primary align-self-center" role="status" />}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {data && (
        <>
          <div className="row g-2 mb-3">
            {stats.map((s) => <RouteCard key={s.rota} s={s} />)}
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-0">Volume por camião (m³)</h6>
              <div className="text-muted small mb-2">Linha = capacidade para reposição. Clicar numa barra abre os produtos do camião.</div>
              {chartData.length === 0 ? <div className="text-muted">Sem camiões no período.</div> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} barGap={2}
                    onClick={(e: any) => {
                      const dia = e?.activePayload?.[0]?.payload?.dia;
                      if (dia) patchParams({ dia, rota: '' }, true);
                    }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} unit=" m³" width={60} />
                    <Tooltip formatter={(v: number, name: string) => [fmtM3(v), name]} cursor={{ fill: 'rgba(0,0,0,.04)' }} />
                    <Legend />
                    {capacity && (
                      <ReferenceLine y={capacity} stroke="#52514e" strokeDasharray="4 4"
                        label={{ value: `Capacidade ${fmtM3(capacity)}`, position: 'insideTopRight', fontSize: 11, fill: '#52514e' }} />
                    )}
                    {TRUCK_ROUTES.map((r) => (
                      <Bar key={r} dataKey={r} name={r} fill={ROUTE_COLORS[r]} radius={[4, 4, 0, 0]} maxBarSize={18} cursor="pointer" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-xl-5">
              <div className="card h-100">
                <div className="card-body">
                  <h6>Por semana</h6>
                  <div className="table-responsive">
                    <table className="table table-sm small">
                      <thead className="table-light">
                        <tr><th>Semana</th><th>Rota</th><th className="text-end">Camiões</th><th className="text-end">Unid.</th>
                          <th className="text-end">m³ médio</th><th className="text-end">m³ máx.</th><th>Ocupação média</th></tr>
                      </thead>
                      <tbody>
                        {weeks.map((w) => (
                          <tr key={`${w.semana}-${w.rota}`}>
                            <td className="text-nowrap">{fmtDay(w.semana)}–{fmtDay(shiftDate(w.semana, 6))}</td>
                            <td>{w.rota}</td>
                            <td className="text-end">{w.n}</td>
                            <td className="text-end">{fmtInt(w.un)}</td>
                            <td className="text-end">{fmtM3(w.m3 / w.n)}</td>
                            <td className="text-end">{fmtM3(w.max)}</td>
                            <td><OccBar pct={w.pct.length ? w.pct.reduce((a, b) => a + b, 0) / w.pct.length : null} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-xl-7">
              <div className="card h-100">
                <div className="card-body">
                  <h6>Camiões</h6>
                  <div className="table-responsive" style={{ maxHeight: 600 }}>
                    <table className="table table-sm table-hover small">
                      <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                        <tr><th>Dia</th><th>Rota</th><th>Lojas</th><th className="text-end">Unid.</th>
                          <th className="text-end">Volume</th><th className="text-end">Paletes</th><th>Ocupação</th></tr>
                      </thead>
                      <tbody>
                        {recent.map((t) => (
                          <tr key={`${t.dia}-${t.rota}`} style={{ cursor: 'pointer' }}
                            onClick={() => patchParams({ dia: t.dia, rota: t.rota }, true)}>
                            <td className="text-nowrap">{WEEKDAYS[t.dia_semana]} {fmtDate(t.dia)}</td>
                            <td>{t.rota}</td>
                            <td className="text-nowrap">
                              {t.lojas.map((l) => `${storeName(l.loja)} ${l.m3.toLocaleString('pt-PT', { maximumFractionDigits: 1 })}`).join(' · ')}
                            </td>
                            <td className="text-end">{fmtInt(t.unidades)}</td>
                            <td className="text-end" title={t.m3_imputado ? `${fmtM3(t.m3_imputado)} estimados (sem volume ou volume anómalo no SAP)` : undefined}>
                              {fmtM3(t.m3)}
                            </td>
                            <td className="text-end">{t.paletes_equiv.toLocaleString('pt-PT')}{t.paletes_capacidade ? ` / ${t.paletes_capacidade}` : ''}</td>
                            <td><OccBar pct={t.ocupacao_pct} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {dayParam && ISO_DATE.test(dayParam) && (
        <DayModal dia={dayParam} rota={routeParam} onClose={() => patchParams({ dia: '', rota: '' })} />
      )}
    </div>
  );
};

export default StockTrucksPage;
